/**
 * AnthropometryEditorDialog — Phase 2.2B.1 (Anthropometry Editor)
 *
 * Endpoint: POST /api/nutrition/anthropometry
 * Body shape (per backend/schemas/nutrition.py AnthropometryCreate):
 *   { athlete_id, date, weight_kg, height_cm?, body_fat_pct?, lean_mass_kg?,
 *     fat_mass_kg?, bmr_kcal?, tdee_kcal?, bmr_formula?, activity_level?,
 *     activity_factor?, notes? }
 *
 * Backend enforces APPEND-ONLY history + future-date guard + range validation.
 * Backend write access via assert_can_write_athlete.
 */
import React, { useState } from 'react';
import { X, Loader2, Save, Scale } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';

const todayIso = () => new Date().toISOString().slice(0, 10);

const NumField = ({ label, value, onChange, testid, placeholder, suffix, required = false, min, max, step = '0.1' }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-zinc-400 mb-1 block">
      {label}{required && <span className="text-red-400 ml-1">*</span>}
    </label>
    <div className="relative">
      <input
        type="number"
        step={step}
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-cyan-600 pr-12"
        data-testid={testid}
      />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-500 pointer-events-none">{suffix}</span>}
    </div>
  </div>
);

const AnthropometryEditorDialog = ({ open, onClose, athleteId, athleteName, heightHint, onSaved }) => {
  const { api } = useAuth();
  const { language } = useLanguage();
  const [date, setDate] = useState(todayIso());
  const [weight, setWeight] = useState('');
  const [bf, setBf] = useState('');
  const [lean, setLean] = useState('');
  const [fat, setFat] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setDate(todayIso());
    setWeight(''); setBf(''); setLean(''); setFat(''); setNotes('');
    setError(null);
  };

  const handleClose = () => { reset(); onClose(); };

  if (!open) return null;

  const handleSave = async () => {
    if (!weight) {
      setError(language === 'IT' ? 'Il peso è obbligatorio' : 'Weight is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        athlete_id: athleteId,
        date,
        weight_kg: parseFloat(weight),
      };
      if (bf !== '') payload.body_fat_pct = parseFloat(bf);
      if (lean !== '') payload.lean_mass_kg = parseFloat(lean);
      if (fat !== '') payload.fat_mass_kg = parseFloat(fat);
      if (notes.trim()) payload.notes = notes.trim();

      const res = await api.post('/nutrition/anthropometry', payload);
      onSaved && onSaved(res.data);
      reset();
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(Array.isArray(detail) ? detail.map(d => d.msg).join('; ') : (detail || (language === 'IT' ? 'Salvataggio fallito' : 'Save failed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="anthropometry-editor-dialog">
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        <header className="sticky top-0 bg-zinc-900 border-b border-zinc-800 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <Scale className="w-5 h-5 text-cyan-400" />
            <div>
              <h2 className="text-lg font-semibold text-zinc-100">
                {language === 'IT' ? 'Nuova misurazione' : 'New measurement'}
              </h2>
              <p className="text-xs text-zinc-500 mt-0.5">{athleteName}{heightHint ? ` · ${heightHint} cm` : ''}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleClose} data-testid="anthro-editor-close">
            <X className="w-4 h-4" />
          </Button>
        </header>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-400 mb-1 block">
                {language === 'IT' ? 'Data' : 'Date'}<span className="text-red-400 ml-1">*</span>
              </label>
              <input
                type="date"
                value={date}
                max={todayIso()}
                onChange={(e) => setDate(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-200 focus:outline-none focus:border-cyan-600"
                data-testid="anthro-date"
              />
            </div>
            <NumField
              label={language === 'IT' ? 'Peso' : 'Weight'}
              value={weight} onChange={setWeight}
              testid="anthro-weight" suffix="kg"
              placeholder="e.g. 78.5" required min={1} max={299}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <NumField
              label="BF %" value={bf} onChange={setBf}
              testid="anthro-bf" suffix="%"
              placeholder="e.g. 14.2" min={0} max={70}
            />
            <NumField
              label={language === 'IT' ? 'Massa magra' : 'Lean mass'}
              value={lean} onChange={setLean}
              testid="anthro-lean" suffix="kg"
              placeholder="e.g. 65.0" min={0} max={249}
            />
            <NumField
              label={language === 'IT' ? 'Massa grassa' : 'Fat mass'}
              value={fat} onChange={setFat}
              testid="anthro-fat" suffix="kg"
              placeholder="e.g. 11.0" min={0} max={199}
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-400 mb-1 block">
              {language === 'IT' ? 'Note' : 'Notes'}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder={language === 'IT' ? 'Condizioni del test, contesto…' : 'Test conditions, context…'}
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-cyan-600 resize-none"
              data-testid="anthro-notes"
            />
          </div>

          <p className="text-xs text-zinc-500 italic">
            {language === 'IT'
              ? 'Append-only: ogni misurazione è uno snapshot storico. BMI, BMR e TDEE vengono calcolati automaticamente.'
              : 'Append-only: each measurement is a historical snapshot. BMI, BMR and TDEE are computed automatically.'}
          </p>

          {error && (
            <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2" data-testid="anthro-editor-error">
              {error}
            </div>
          )}
        </div>

        <footer className="sticky bottom-0 bg-zinc-900 border-t border-zinc-800 px-6 py-3 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={handleClose} disabled={saving} data-testid="anthro-editor-cancel">
            {language === 'IT' ? 'Annulla' : 'Cancel'}
          </Button>
          <Button onClick={handleSave} disabled={saving || !weight} data-testid="anthro-editor-save">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {language === 'IT' ? 'Salva' : 'Save'}
          </Button>
        </footer>
      </div>
    </div>
  );
};

export default AnthropometryEditorDialog;
